'use client';

import type { ReactNode } from "react";
import { useState } from "react";
import { useAuth } from "../../providers/AuthProvider";
import { Header } from "./Header";
import { ImpersonationBanner } from "./ImpersonationBanner";
import { Sidebar } from "./Sidebar";
import { BottomNav } from "./BottomNav";
import type { SidebarItem } from "./types";

interface MainLayoutProps {
  children: ReactNode;
  sidebarItems: SidebarItem[];
  activePath?: string;
  isSidebarCollapsed: boolean;
  onSidebarToggle: () => void;
  sidebarRenderItem?: Parameters<typeof Sidebar>[0]["renderItem"];
  headerTitle?: string;
  headerSearchPlaceholder?: string;
  headerLeftSlot?: ReactNode;
  headerRightSlot?: ReactNode;
}

export function MainLayout({
  children,
  sidebarItems,
  activePath,
  isSidebarCollapsed,
  onSidebarToggle,
  sidebarRenderItem,
  headerTitle,
  headerSearchPlaceholder,
  headerLeftSlot,
  headerRightSlot,
}: MainLayoutProps) {
  const { user } = useAuth();
  const isSuperAdmin = user?.role === "super_admin";
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div className="flex h-screen bg-muted/40">
      <Sidebar
        items={sidebarItems}
        activePath={activePath}
        isCollapsed={isSidebarCollapsed}
        onToggle={onSidebarToggle}
        renderItem={sidebarRenderItem}
        mobileNavOpen={mobileNavOpen}
        onMobileNavOpenChange={setMobileNavOpen}
      />
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        <Header
          title={headerTitle}
          searchPlaceholder={headerSearchPlaceholder}
          leftSlot={headerLeftSlot}
          rightSlot={headerRightSlot}
          sidebarItems={sidebarItems}
          onOpenMobileNav={() => setMobileNavOpen(true)}
        />
        <ImpersonationBanner />
        <main className="flex-1 overflow-y-auto pb-[calc(4.5rem+env(safe-area-inset-bottom))] md:pb-0">
          {children}
        </main>
        <BottomNav items={sidebarItems} isSuperAdmin={isSuperAdmin} />
      </div>
    </div>
  );
}
